import React, { useState, useEffect, useCallback } from 'react';
import { 
  ITextInputComponent, 
  INumberInputComponent, 
  IEmailInputComponent, 
  TInputComponentType,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';

interface DfFormInputProps {
  id: string;
  properties: ITextInputComponent | INumberInputComponent | IEmailInputComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string;
  inputType?: TInputComponentType;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
}

export const DfFormInput: React.FC<DfFormInputProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  inputType = 'text',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'test',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false
}) => {
  const [value, setValue] = useState<string>(formValue || properties?.basic?.defaultValue || '');
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // Determine input type based on component name
  const getInputType = (): TInputComponentType => {
    if (inputType) return inputType;
    
    const name = properties?.name;
    if (name === 'text-input') return 'text';
    if (name === 'number-input') return 'number';
    if (name === 'email-input') return 'email';
    
    return 'text';
  };

  // Build validation rules
  const buildValidationRules = () => {
    const rules: any = {};
    
    if (properties?.validation?.required) {
      rules.required = true;
    }
    
    if (getInputType() === 'email') {
      rules.email = true;
    }
    
    if (getInputType() === 'text') {
      const textValidation = properties.validation as ITextInputComponent['validation'];
      if (textValidation?.minLength) rules.minLength = textValidation.minLength;
      if (textValidation?.maxLength) rules.maxLength = textValidation.maxLength;
    }
    
    if (getInputType() === 'number') {
      const numberValidation = properties.validation as INumberInputComponent['validation'];
      if (numberValidation?.min) rules.min = numberValidation.min;
      if (numberValidation?.max) rules.max = numberValidation.max;
      if (numberValidation?.minLength) rules.minLength = numberValidation.minLength;
      if (numberValidation?.maxLength) rules.maxLength = numberValidation.maxLength;
    }
    
    return rules;
  };

  // Validate field
  const validateField = useCallback((val: string): { isValid: boolean; errors: Record<string, any> } => {
    const rules = buildValidationRules();
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (rules.required && (!val || val.trim() === '')) {
      errors.required = true;
      isValid = false;
    }

    // Email validation
    if (rules.email && val && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val)) {
      errors.email = true;
      isValid = false;
    }

    // Length validations
    if (rules.minLength && val && val.length < rules.minLength) {
      errors.minLength = true;
      isValid = false;
    }

    if (rules.maxLength && val && val.length > rules.maxLength) {
      errors.maxLength = true;
      isValid = false;
    }

    // Number validations
    if (getInputType() === 'number' && val) {
      const numValue = parseFloat(val);
      if (isNaN(numValue)) {
        errors.pattern = true;
        isValid = false;
      } else {
        if (rules.min !== undefined && numValue < rules.min) {
          errors.min = true;
          isValid = false;
        }
        if (rules.max !== undefined && numValue > rules.max) {
          errors.max = true;
          isValid = false;
        }
      }
    }

    return { isValid, errors };
  }, [properties, getInputType]);

  // Handle value changes
  const handleValueChange = useCallback((newValue: string) => {
    setValue(newValue);
    
    if (onValueChange) {
      const validation = validateField(newValue);
      const change: IFormControlChange = {
        id,
        value: newValue,
        isValid: validation.isValid,
        errors: validation.errors,
      };
      onValueChange(change);
    }
  }, [id, onValueChange, validateField]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    handleValueChange(newValue);
  };

  // Handle focus
  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    
    setIsFocused(false);
    setIsTouched(true);
    
    // Always trigger validation on blur in test mode for any field with validation rules
    if (mode === 'test' && onValueChange) {
      const validation = validateField(value);
      const change: IFormControlChange = {
        id,
        value: value,
        isValid: validation.isValid,
        errors: validation.errors,
      };
      
      onValueChange(change);
    }
    
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Reset touched state and value when switching modes
  useEffect(() => {
    if (mode === 'edit') {
      setIsTouched(false);
      // Reset value to default value when switching to edit mode
      const defaultValue = properties?.basic?.defaultValue || '';
      setValue(defaultValue);
    } else if (mode === 'test') {
      setIsTouched(false);
      // Reset value to empty when switching to test mode for fresh start
      setValue('');
    }
  }, [mode, properties?.basic?.defaultValue]);

  // Update value when formValue prop changes (but don't override user input)
  useEffect(() => {
    const newValue = formValue || properties?.basic?.defaultValue || '';
    // Only update if the new value is different and we're not currently focused
    // Also, don't override with empty value if user has typed something
    // CRITICAL FIX: Don't reset user input when formValue becomes empty
    if (newValue !== value && !isFocused && !(newValue === '' && value.trim() !== '')) {
      setValue(newValue);
    } else if (newValue === '' && value.trim() !== '' && !isFocused) {
    }
  }, [formValue, value, isFocused, properties?.basic?.defaultValue]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Clear value when switching away from test mode
  useEffect(() => {
    if (mode !== 'test') {
      setValue('');
    }
  }, [mode]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get input classes
  const getInputClasses = (): string => {
    const base = 'form-control';
    
    // Only show validation errors in test mode
    if (mode === 'test') {
      const validation = validateField(value);
      const hasLocalError = !validation.isValid && isTouched;
      const hasExternalError = validationErrors[id] && (isTouched || formSubmitted);
      const error = (hasLocalError || hasExternalError) ? 'is-invalid' : '';
      return `${base} ${error} ${className}`.trim();
    }
    
    return `${base} ${className}`.trim();
  };


  const currentInputType = getInputType();
  const shouldDisable = disabled || readonly || properties?.validation?.readonly;

  return (
    <div className={`form-group ${getLabelAlignmentClass()}`}>
      {!hideLabel && properties.basic.label && (
        <label htmlFor={id} className="form-label">
          {properties.basic.label}
          {properties.validation.required && (
            <span className="required-indicator">*</span>
          )}
        </label>
      )}

      <div className="input-wrapper">
        <input
          type={currentInputType}
          id={id}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={properties.basic.placeholder}
          disabled={shouldDisable}
          readOnly={readonly || properties?.validation?.readonly}
          className={getInputClasses()}
        />

        <DfFormErrorMsg
          validationErrors={validationErrors}
          fieldId={id}
          touchedFields={touchedFields}
          formSubmitted={formSubmitted}
          properties={properties}
          localValidation={validateField(value)}
          isTouched={isTouched}
          mode={mode}
        />
      </div>
    </div>
  );
};

export default DfFormInput;
